/**
 * CartRecovery — shared helpers for surviving the edge bot-protection 429
 * checkpoint on cart writes ("Verifying your connection..." + Clear-Site-Data).
 *
 * Three cart write flows can hit the same checkpoint:
 *   1. delivery-method switch  (sections/main-cart-items.liquid)
 *   2. quantity change         (assets/scripts.js CartItems.updateQuantity)
 *   3. recommendation add      (assets/product-form.js, cart recommendations)
 *
 * The checkpoint only clears on a top-level navigation, so the recovery for all
 * three is identical: persist what the user tried to do, reload (capped), and
 * replay it once the page comes back. This module is the single source of truth
 * for that pattern plus the "grey-out siblings while a write is in flight" UI
 * lock that stops a second concurrent write (the thing that trips the 429).
 *
 * Load order: after cart-queue.js, before cart-store/cart-api/scripts/custom.
 */
window.CartRecovery = (function () {
  'use strict';

  // sessionStorage key namespace. One entry per flow ("delivery", "qty",
  // "reco_add"); each stores { state: <caller payload>, count: <reloads used> }.
  var PREFIX = 'bm_recovery_';

  function _key(name) { return PREFIX + name; }

  // True when a fetch Response carries the 429 edge checkpoint status, or when a
  // raw body string contains the checkpoint marker (callers that already read
  // response.status should pass the Response).
  function is429(x) {
    if (!x) return false;
    if (typeof x.status === 'number') return x.status === 429;
    if (typeof x === 'string') return /verifying your connection|just a moment/i.test(x);
    return false;
  }

  function _read(name) {
    var raw = sessionStorage.getItem(_key(name));
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /**
   * Persist `state` and reload so the action replays after the checkpoint
   * clears. Capped at `max` reloads (default 2) so a persistent checkpoint can
   * never loop. The reload counter survives across reloads because it is only
   * cleared on success (clearReplay) or when the budget is exhausted here.
   *
   * Returns true if the page is reloading (caller MUST halt), false if the
   * budget is spent (caller should fall back to showing an error).
   */
  function reloadAndReplay(name, state, max) {
    if (typeof max !== 'number') max = 2;
    var prev = _read(name);
    var count = (prev && prev.count) || 0;
    if (count < max) {
      sessionStorage.setItem(_key(name), JSON.stringify({ state: state || {}, count: count + 1 }));
      window.location.reload();
      return true;
    }
    clearReplay(name); // budget spent — caller handles the failure
    return false;
  }

  // Return the saved replay payload ({...caller state}) or null. Does NOT clear,
  // so the reload counter is preserved if the replay attempt also 429s.
  function getReplay(name) {
    var rec = _read(name);
    return rec ? (rec.state || null) : null;
  }

  function clearReplay(name) { sessionStorage.removeItem(_key(name)); }

  /**
   * Serialize a FormData into a plain { key: value } object suitable for JSON
   * persistence and later replay. Skips the section-render fields so the
   * replayed /cart/add doesn't re-trigger section rendering, but PRESERVES
   * line-item `properties[...]` (and selling_plan / any custom fields). Keeping
   * the properties is what lets the replayed add MERGE into the existing line
   * item — an add that drops them looks like a different line to Shopify and
   * creates a duplicate row instead of incrementing quantity.
   */
  function serializeForm(fd) {
    var obj = {};
    if (!fd || typeof fd.forEach !== 'function') return obj;
    fd.forEach(function (value, key) {
      if (key === 'sections' || key === 'sections_url') return;
      obj[key] = value;
    });
    return obj;
  }

  // Rebuild a FormData from a serializeForm() payload for replaying /cart/add.
  function toFormData(payload) {
    var fd = new FormData();
    if (!payload) return fd;
    Object.keys(payload).forEach(function (k) { fd.append(k, payload[k]); });
    return fd;
  }

  // ── UI lock ────────────────────────────────────────────────────────────────
  // Grey-out + disable a group of elements while a write is in flight so the
  // user cannot start a second concurrent cart write. Mirrors the delivery-radio
  // lock. Accepts a NodeList, array, or single element.
  var FADE_CLASS = 'cart-write-pending';

  function _forEach(list, fn) {
    if (!list) return;
    if (list.nodeType) { fn(list); return; }
    Array.prototype.forEach.call(list, fn);
  }

  function fadeGroup(elements) {
    _forEach(elements, function (el) {
      if (!el) return;
      el.classList.add(FADE_CLASS);
      if ('disabled' in el) el.disabled = true;
      el.setAttribute('aria-disabled', 'true');
    });
  }

  function unfadeGroup(elements) {
    _forEach(elements, function (el) {
      if (!el) return;
      el.classList.remove(FADE_CLASS);
      if ('disabled' in el) el.disabled = false;
      el.removeAttribute('aria-disabled');
    });
  }

  return {
    is429: is429,
    reloadAndReplay: reloadAndReplay,
    getReplay: getReplay,
    clearReplay: clearReplay,
    serializeForm: serializeForm,
    toFormData: toFormData,
    fadeGroup: fadeGroup,
    unfadeGroup: unfadeGroup,
    FADE_CLASS: FADE_CLASS
  };
})();
